import React, { useRef, useCallback, useMemo, useEffect } from 'react';
import { isAndroid } from 'react-device-detect';
import { createRoot } from 'react-dom/client';
import { useDispatch } from 'react-redux';
import { snackbarActions } from 'redux/snackbar';

// Capture default true only on Android
// (Android & capture=true) Gives the option to choose from photos, files or camera
// (Apple & capture=true) Takes directly to the camera without the option to choose from existing photos
const captureDefault = isAndroid ? true : undefined;
const useFileInput = ({ onChange, capture = captureDefault, ...config } = {}) => {

  const dispatch = useDispatch();

  const inputRef = useRef();

  const [parent, parentRoot] = useMemo(() => {
    const element = document.createElement('div');
    element.style.display = 'none';

    return [element, createRoot(element)];
  }, []);

  useEffect(() => {
    document.body.appendChild(parent);
    return () => {
      document.body.removeChild(parent);
    };
  }, [parent]);

  const onFileChange = useCallback(
    ({ target: { files } }) => {
      const selected = Array.from(files);
      const nonEmpty = selected.filter(file => file.size > 0);
      inputRef.current.value = '';

      if (nonEmpty.length < selected.length) {
        dispatch(snackbarActions.createFailure('error.emptyFile', { translate: true }));
        if (nonEmpty.length === 0) return;
      }

      onChange(config.multiple ? nonEmpty : nonEmpty[0]);
    },
    [config, onChange, dispatch],
  );

  const child = useMemo(() => <input type="file" capture={capture} ref={inputRef} {...config} onChange={onFileChange} />, [capture, config, onFileChange]);

  useEffect(() => {
    parentRoot.render(child);
  }, [child, parentRoot]);

  const open = useCallback(() => {
    inputRef.current.click();
  }, []);

  return { open };
};

export default useFileInput;
